// covered-by: frontends/carrie/src/components/ask-puntt-review/__tests__/linkedArtworkView.test.tsx
//
// SPIKE (side-by-side version compare): two artwork viewers that move as one.
// Zoom, scroll, rotation and page are each viewer's own state, deep inside
// `AssetViewerPane`; this is the smallest thing that lets two of them share it
// without lifting that state out of a 5,000-line file. A viewer mounted under
// the provider publishes what the person did to it and applies what its peer
// publishes; outside the provider the hook does nothing.
import {
  createContext,
  type Dispatch,
  type ReactNode,
  type RefObject,
  type SetStateAction,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";

type Rotations = Record<number, 0 | 90 | 180 | 270>;

type LinkedViewPatch = {
  zoom?: number;
  /** Scroll position as a fraction of what can be scrolled, each axis 0..1. */
  scroll?: { fx: number; fy: number };
  rotations?: Rotations;
  page?: number;
};

type Listener = (patch: LinkedViewPatch) => void;

export class LinkedViewChannel {
  private listeners = new Map<string, Listener>();
  // why kept: the other version's viewer mounts after its review and its file
  // have loaded, long after this one published where it stands. A late
  // subscriber is handed the view as it is rather than starting at its own.
  private view: LinkedViewPatch = {};

  subscribe(id: string, listener: Listener): () => void {
    this.listeners.set(id, listener);
    if (Object.keys(this.view).length > 0) listener(this.view);
    return () => {
      this.listeners.delete(id);
    };
  }

  publish(from: string, patch: LinkedViewPatch): void {
    this.view = { ...this.view, ...patch };
    this.listeners.forEach((listener, id) => {
      if (id !== from) listener(patch);
    });
  }
}

const LinkedArtworkViewContext = createContext<LinkedViewChannel | null>(null);

export function LinkedArtworkViewProvider({
  enabled = true,
  children,
}: {
  enabled?: boolean;
  children: ReactNode;
}) {
  const channel = useMemo(() => new LinkedViewChannel(), []);
  return (
    <LinkedArtworkViewContext.Provider value={enabled ? channel : null}>
      {children}
    </LinkedArtworkViewContext.Provider>
  );
}

// why: a scroll the peer caused fires `scroll` here too. Without a window in
// which those events are not republished the two viewers answer each other
// for ever.
const REMOTE_SCROLL_QUIET_MS = 120;

// why: selecting a finding makes EACH viewer zoom to it and then scroll to it
// smoothly, on its own. Linked, the two sent each other every half-way
// position of that glide and the zoom's pre-glide scroll, so each kept
// dragging the other off the finding. While a viewer settles on a new focus
// it neither sends nor takes zoom and scroll; both arrive by themselves.
const FOCUS_SETTLE_MS = 900;

function fraction(position: number, size: number, viewport: number): number {
  const room = size - viewport;
  return room > 0 ? Math.min(1, Math.max(0, position / room)) : 0;
}

export function useLinkedArtworkView<R extends Rotations>({
  scrollRef,
  zoom,
  setZoom,
  rotations,
  setRotations,
  page,
  setPage,
  focusKey = null,
}: {
  scrollRef: RefObject<HTMLElement>;
  zoom: number;
  setZoom: Dispatch<SetStateAction<number>>;
  rotations: R;
  setRotations: Dispatch<SetStateAction<R>>;
  page: number;
  setPage: (page: number) => void;
  /** What the viewer is focused on (the active finding); a change starts a settle. */
  focusKey?: string | null;
}): void {
  const channel = useContext(LinkedArtworkViewContext);
  const id = useId();
  // What the peer last sent, so applying it is not published straight back.
  const remote = useRef<{ zoom?: number; rotations?: string; page?: number }>(
    {},
  );
  const quietUntil = useRef(0);
  const pendingScroll = useRef<{ fx: number; fy: number } | null>(null);
  const zoomNow = useRef(zoom);
  useLayoutEffect(() => {
    zoomNow.current = zoom;
  }, [zoom]);
  const setPageRef = useRef(setPage);
  setPageRef.current = setPage;
  // why: a viewer that has just mounted stands at its defaults, and publishing
  // those would reset the version already on screen. It publishes from its
  // first change on; a viewer already mounted when a comparison opens says
  // where it stands at once.
  const settled = useRef(false);
  const settlingUntil = useRef(0);
  const firstFocus = useRef(true);

  // Layout, and declared first: it must be set before the viewer's own focus
  // effect asks for the zoom this commit.
  useLayoutEffect(() => {
    if (firstFocus.current) {
      firstFocus.current = false;
      if (focusKey == null) return;
    }
    settlingUntil.current = performance.now() + FOCUS_SETTLE_MS;
  }, [focusKey]);

  useEffect(() => {
    if (!channel) return;
    const applyScroll = (scroll: { fx: number; fy: number }) => {
      const element = scrollRef.current;
      if (!element) return;
      quietUntil.current = performance.now() + REMOTE_SCROLL_QUIET_MS;
      element.scrollLeft =
        scroll.fx * (element.scrollWidth - element.clientWidth);
      element.scrollTop =
        scroll.fy * (element.scrollHeight - element.clientHeight);
    };
    return channel.subscribe(id, (patch) => {
      if (patch.page != null) {
        remote.current.page = patch.page;
        setPageRef.current(patch.page);
      }
      if (patch.rotations) {
        remote.current.rotations = JSON.stringify(patch.rotations);
        setRotations(patch.rotations as R);
      }
      if (performance.now() < settlingUntil.current) return;
      // why not on an equal zoom: setting it again renders nothing, so the
      // effect that applies a pending scroll would never run.
      if (patch.zoom != null && patch.zoom === zoomNow.current) {
        remote.current.zoom = patch.zoom;
      } else if (patch.zoom != null) {
        remote.current.zoom = patch.zoom;
        // The scroll belongs to the zoomed layout, which does not exist yet.
        pendingScroll.current = patch.scroll ?? null;
        quietUntil.current = performance.now() + REMOTE_SCROLL_QUIET_MS;
        setZoom(patch.zoom);
        return;
      }
      if (patch.scroll) applyScroll(patch.scroll);
    });
  }, [channel, id, scrollRef, setRotations, setZoom]);

  // The peer's scroll, once this viewer has laid out at the peer's zoom.
  useLayoutEffect(() => {
    const scroll = pendingScroll.current;
    const element = scrollRef.current;
    if (!scroll || !element) return;
    pendingScroll.current = null;
    quietUntil.current = performance.now() + REMOTE_SCROLL_QUIET_MS;
    element.scrollLeft =
      scroll.fx * (element.scrollWidth - element.clientWidth);
    element.scrollTop =
      scroll.fy * (element.scrollHeight - element.clientHeight);
  }, [zoom, scrollRef]);

  useEffect(() => {
    if (!channel || !settled.current || remote.current.zoom === zoom) return;
    if (performance.now() < settlingUntil.current) return;
    const element = scrollRef.current;
    channel.publish(id, {
      zoom,
      scroll: element
        ? {
            fx: fraction(
              element.scrollLeft,
              element.scrollWidth,
              element.clientWidth,
            ),
            fy: fraction(
              element.scrollTop,
              element.scrollHeight,
              element.clientHeight,
            ),
          }
        : undefined,
    });
  }, [channel, id, scrollRef, zoom]);

  useEffect(() => {
    if (!channel || !settled.current) return;
    const serialised = JSON.stringify(rotations);
    if (remote.current.rotations === serialised || serialised === "{}") return;
    channel.publish(id, { rotations });
  }, [channel, id, rotations]);

  useEffect(() => {
    if (!channel || !settled.current || remote.current.page === page) return;
    channel.publish(id, { page });
  }, [channel, id, page]);

  useEffect(() => {
    const element = scrollRef.current;
    if (!channel || !element) return;
    let frame = 0;
    const onScroll = () => {
      if (performance.now() < quietUntil.current) return;
      if (performance.now() < settlingUntil.current) return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        channel.publish(id, {
          scroll: {
            fx: fraction(
              element.scrollLeft,
              element.scrollWidth,
              element.clientWidth,
            ),
            fy: fraction(
              element.scrollTop,
              element.scrollHeight,
              element.clientHeight,
            ),
          },
        });
      });
    };
    element.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      element.removeEventListener("scroll", onScroll);
    };
  }, [channel, id, scrollRef]);

  // Last on purpose: effects run in the order they are declared, so the three
  // publishing effects above have each skipped their first pass by now.
  useEffect(() => {
    settled.current = true;
  }, []);
}
